import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { inputClass, Honeypot, ConsentNote } from "@/components/Shared";
import { postEnquiry } from "@/lib/api";
import { trackEvent, leadContext } from "@/lib/analytics";
import { CLINIC } from "@/data/content";
import { getHairStage, isHairPage } from "@/lib/hairStage";
import { HairStageChip } from "@/components/HairStageChip";

const BEST_TIMES = ["Morning (9am–12pm)", "Midday (12–2pm)", "Afternoon (2–6pm)", "Any time"];

const CallbackForm = ({ page, source, practitioner, onSubmitted }) => {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();
  const [done, setDone] = useState(false);

  const onSubmit = async (data) => {
    try {
      const lead = leadContext("callback_requested", `${practitioner} · ${source}`);
      const stage = isHairPage(page) ? getHairStage() : "";
      await postEnquiry("callback", { ...data, page, source, practitioner, ...(stage ? { stage } : {}), meta: lead });
      trackEvent("callback_requested", { page, source }, lead);
      onSubmitted?.();
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please call us on " + CLINIC.phone + ".");
    }
  };

  if (done) {
    return (
      <div className="bg-cream p-6" data-testid="callback-form-success">
        <p className="font-serif text-deep-sea text-2xl">Thank you.</p>
        <p className="text-ink/75 text-sm mt-2 leading-relaxed">We'll call within one business day from {CLINIC.phone}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" data-testid="callback-form" noValidate>
      <Honeypot register={register} />
      {isHairPage(page) && <HairStageChip />}
      <input type="hidden" value={source} data-testid="callback-source-input" {...register("source")} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="text" required placeholder="Name" autoComplete="name" data-testid="callback-name-input" className={inputClass} {...register("name", { required: true })} />
        <input type="tel" required placeholder="Mobile" autoComplete="tel" data-testid="callback-phone-input" className={inputClass} {...register("phone", { required: true })} />
      </div>
      <input type="email" required placeholder="Email" autoComplete="email" data-testid="callback-email-input" className={inputClass} {...register("email", { required: true })} />
      <select required defaultValue="" data-testid="callback-best_time-input" className={inputClass} {...register("best_time", { required: true })}>
        <option value="" disabled>Best time to call…</option>
        {BEST_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label className="flex items-start gap-3 text-xs text-ink/75 leading-relaxed">
        <input type="checkbox" required data-testid="callback-consent-checkbox" className="mt-0.5 accent-[#A68A5B]" {...register("consent", { required: true })} />
        <span>I consent to Lumière Aesthetics contacting me about my enquiry.</span>
      </label>
      <button type="submit" disabled={isSubmitting} className="btn-gold w-full py-3.5 text-sm font-medium tracking-[0.06em] disabled:opacity-60" data-testid="callback-submit-button">
        {isSubmitting ? "Sending…" : "Request a callback"}
      </button>
      <p className="text-soft-grey text-xs" data-testid="callback-promise">We'll call within one business day from {CLINIC.phone}.</p>
      <ConsentNote />
    </form>
  );
};

export default CallbackForm;
